import styled from '@emotion/styled';
import { Fragment, useEffect, useState } from 'react';
import { useTestContext } from '../../../context/testContext';
import { eyeOptions, correctedOptions, letterOptions } from '../../../helpers/data';
import { colors, fonts, breakpoints, PanelTitle, SelectField, RadioGroup, SessionActions } from '../../../ui';

// Step 1 "Setup" of the acuity tests (Figma session/10 left, 13 left, 15 right): Reminder, Select
// Eye, Corrected / Uncorrected, Letters to use (logMAR) and the Crowding / Optotypes radios (Kay's).
// Same test-context writes as screens/optician/eye-setup.js.

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;
  width: 100%;
  max-width: 520px;
  margin: auto;
  font-family: ${fonts.family};
  @media (max-width: ${breakpoints.phone - 1}px) {
    gap: 20px;
  }
`;

const Reminder = styled.p`
  margin: -4px 0 0;
  min-height: 1.6em;
  font-size: 14px;
  line-height: 1.6;
  color: ${colors.slate[600]};
  text-align: center;
  b {
    font-weight: 700;
    color: ${colors.slate[800]};
  }
`;

const Fields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px 16px;
  width: 100%;
  .full {
    grid-column: 1 / -1;
  }
  @media (max-width: ${breakpoints.phone - 1}px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const GroupLabel = styled.span`
  display: block;
  margin-bottom: 8px;
  font-size: 12px;
  line-height: 20px;
  font-weight: 700;
  color: ${colors.slate[700]};
`;

/** "Ask patient to patch/cover their <b>right eye</b> as it is not being tested." */
export const reminderFor = (eye) => {
  if (!eye) return 'Select the eye to be tested.';
  if (eye.includes('And')) {
    return (
      <>
        Ask patient to patch/cover their <b>left eye</b> for the first test, then their <b>right eye</b> for the second.
      </>
    );
  }
  const opposite = eye === 'left' ? 'right eye' : 'left eye';
  return (
    <>
      Ask patient to patch/cover their <b>{opposite}</b> as it is not being tested.
    </>
  );
};

/** Presentational setup form (also used by the dev showcase). */
export const EyeSetupView = ({ eyeValue, onEye, correctedValue, onCorrected, eyeChoices = eyeOptions, letters, crowding, optotypes, reminderEye }) => (
  <Wrap data-testid='exam-eye-setup'>
    <div style={{ textAlign: 'center' }}>
      <PanelTitle small>Reminder</PanelTitle>
      <Reminder aria-live='polite'>{eyeValue === 'bothEyes' ? 'Both eyes are tested together.' : reminderFor(reminderEye)}</Reminder>
    </div>
    <Fields>
      <SelectField label='Select Eye' options={eyeChoices} value={eyeValue} onChange={onEye} />
      <SelectField label='Corrected / Uncorrected' options={correctedOptions} value={correctedValue} onChange={onCorrected} />
      {letters && <SelectField className='full' label='Letters to use' options={letterOptions} value={letters.value} onChange={letters.onChange} />}
      {crowding && (
        <div>
          <GroupLabel>Crowding</GroupLabel>
          <RadioGroup
            ariaLabel='Crowding'
            options={[
              { value: 'open', label: 'Open' },
              { value: 'crowded', label: 'Crowded' },
            ]}
            value={crowding.value}
            onChange={crowding.onChange}
          />
        </div>
      )}
      {optotypes && (
        <div>
          <GroupLabel>Optotypes to Show</GroupLabel>
          <RadioGroup
            ariaLabel='Optotypes to show'
            options={[
              { value: 1, label: 'Single' },
              { value: 5, label: 'Multiple' },
            ]}
            value={optotypes.value}
            onChange={optotypes.onChange}
          />
        </div>
      )}
    </Fields>
  </Wrap>
);

const ExamEyeSetup = ({ updateStep, selectCrowding, selectLetters, selectOptotypeAmount }) => {
  const [valuesSelected, setValuesSelected] = useState([]);
  const [eyeName, setEyeName] = useState(null);
  const [nextStep, setNextStep] = useState(null);
  const [canProgress, setCanProgress] = useState(false);
  const [eyeValue, setEyeValue] = useState('');
  const [correctedValue, setCorrectedValue] = useState('');
  const [lettersValue, setLettersValue] = useState('');
  const [crowdingValue, setCrowdingValue] = useState('crowded'); // SelectCrowding default
  const [optotypesToShow, setOptotypesToShow] = useState(5); // SelectOptotypeAmount default
  const eyesNotToTest = { testRightEye: false, testLeftEye: false, testBothEyes: false };
  const {
    testData: { testKey },
    updateTestData,
  } = useTestContext();

  useEffect(() => {
    if (selectCrowding) updateTestData({ kaysCrowding: crowdingValue });
  }, [selectCrowding, crowdingValue, updateTestData]);

  useEffect(() => {
    if (selectOptotypeAmount) updateTestData({ optotypesToShow });
  }, [selectOptotypeAmount, optotypesToShow, updateTestData]);

  const setEyeValues = (newData) => {
    const { nextStep, ...eyesToTest } = newData;
    updateTestData({ ...eyesNotToTest, ...eyesToTest });
    setNextStep(nextStep);
  };

  const handleSelected = (selected) => {
    if (selected.includes('corrected')) {
      setValuesSelected((prev) => {
        return { ...prev, checkCorrectedVision: true };
      });
    } else {
      setEyeName(selected.includes('both') ? null : selected);
      setValuesSelected((prev) => {
        return { ...prev, eyeSelected: true };
      });
    }

    switch (selected) {
      case 'right':
        return setEyeValues({ testRightEye: true, nextStep: 'rightEyeSetup' });
      case 'left':
        return setEyeValues({ testLeftEye: true, nextStep: 'leftEyeSetup' });
      case 'bothEyes':
        return setEyeValues({ testBothEyes: true, nextStep: 'bothEyesSetup' });
      case 'leftAndRight':
        return setEyeValues({ testRightEye: true, testLeftEye: true, nextStep: 'rightEyeSetup' });
      case 'corrected':
        return updateTestData({ corrected: true });
      case 'uncorrected':
        return updateTestData({ corrected: false });
      default:
        break;
    }
  };

  useEffect(() => {
    const selectionStatus = ['eyeSelected', 'checkCorrectedVision', selectLetters].map((val) => {
      return val ? valuesSelected[val] : true;
    });

    const everythingSelected = selectionStatus.every((selection) => selection);

    if (everythingSelected) return setCanProgress(true);
  }, [selectLetters, valuesSelected]);

  return (
    <Fragment>
      <EyeSetupView
        eyeValue={eyeValue}
        reminderEye={eyeName}
        eyeChoices={eyeOptions.filter((opt) => (testKey === 'optimaChart' ? opt.value !== 'leftAndRight' : opt))}
        onEye={(v) => {
          setEyeValue(v);
          handleSelected(v);
        }}
        correctedValue={correctedValue}
        onCorrected={(v) => {
          setCorrectedValue(v);
          handleSelected(v);
        }}
        letters={
          selectLetters
            ? {
                value: lettersValue,
                onChange: (v) => {
                  setLettersValue(v);
                  updateTestData({ lettersToUse: v });
                  setValuesSelected((prev) => ({ ...prev, [selectLetters]: true }));
                },
              }
            : null
        }
        crowding={selectCrowding ? { value: crowdingValue, onChange: setCrowdingValue } : null}
        optotypes={selectOptotypeAmount ? { value: optotypesToShow, onChange: setOptotypesToShow } : null}
      />
      <SessionActions onBack={() => updateTestData({ testKey: null })} primary={{ label: 'Next', onClick: () => updateStep(nextStep), disabled: !canProgress }} />
    </Fragment>
  );
};

export default ExamEyeSetup;
